import { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import dayjs from 'dayjs'
import { getBookings } from '../../../../../Bookings/utils/utils'
import { handleError } from '../../../../../lib/utils'
import CoreEnums from '../../../../../../core/utils/enums'

/**
 * Custom hook to fetch and manage the next upcoming appointment
 * @returns {object} Next appointment data, loading state, and error
 */
const useNextAppointment = () => {
  const [nextAppointment, setNextAppointment] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const state = useSelector((state) => state)
  const currentUser = state.auth.agiliteUser?._id

  const getNextAppointmentQuery = () => {
    // Use a broader query to get today + future appointments, then filter client-side
    // This matches the pattern from PatientBookingsListView more closely
    const today = dayjs().format('YYYY-MM-DD')
    return {
      bookingDate: {
        $gte: today
      },
      // Exclude cancelled and completed appointments
      status: {
        $nin: [CoreEnums.bookingStatuses.cancelled, CoreEnums.bookingStatuses.completed]
      }
    }
  }

  const sortBookingsByDateTime = (bookings) => {
    if (!bookings || !Array.isArray(bookings)) {
      return []
    }

    return bookings.sort((a, b) => {
      // Handle missing startTime values by using dateCreated or default time
      const aStartTime = a.startTime || '00:00'
      const bStartTime = b.startTime || '00:00'

      // For flexi consultations, use dateCreated if no bookingDate
      const aBookingDate = a.bookingDate || a.dateCreated
      const bBookingDate = b.bookingDate || b.dateCreated

      const aDate = dayjs(aBookingDate).isValid()
        ? dayjs(aBookingDate).set('hours', aStartTime.slice(0, 2)).set('minutes', aStartTime.slice(-2))
        : dayjs(a.dateCreated)

      const bDate = dayjs(bBookingDate).isValid()
        ? dayjs(bBookingDate).set('hours', bStartTime.slice(0, 2)).set('minutes', bStartTime.slice(-2))
        : dayjs(b.dateCreated)

      // Sort ascending (earliest first) to get next appointment
      return aDate - bDate
    })
  }

  const fetchNextAppointment = async () => {
    if (!currentUser) return

    setLoading(true)
    setError(null)

    try {
      const query = getNextAppointmentQuery()
      const bookings = await getBookings(state, query, currentUser)

      if (bookings && bookings.length > 0) {
        console.log(
          '🔍 All fetched bookings:',
          bookings.map((b) => ({
            id: b._id,
            bookingDate: b.bookingDate,
            startTime: b.startTime,
            medicalProfName: b.medicalProfName,
            serviceName: b.serviceName,
            isVirtualVisit: b.isVirtualVisit,
            status: b.status
          }))
        )

        // Filter to only future appointments and active statuses
        const futureBookings = bookings.filter((booking) => {
          // First check if appointment status is active (not cancelled or completed)
          const isActiveStatus =
            booking.status !== CoreEnums.bookingStatuses.cancelled &&
            booking.status !== CoreEnums.bookingStatuses.completed

          if (!isActiveStatus) {
            console.log(`❌ Appointment ${booking._id} excluded - Status: ${booking.status}`)
            return false
          }

          const appointmentTime = booking.startTime || dayjs(booking.dateCreated).format('HH:mm')
          const appointmentDate = booking.bookingDate || booking.dateCreated

          if (appointmentDate && dayjs(appointmentDate).isValid()) {
            const [hours, minutes] = appointmentTime.split(':')
            const appointmentDateTime = dayjs(appointmentDate)
              .hour(parseInt(hours) || 0)
              .minute(parseInt(minutes) || 0)

            const isFuture = appointmentDateTime.isAfter(dayjs())
            console.log(
              `🕐 Appointment ${booking._id}: ${appointmentDateTime.format('YYYY-MM-DD HH:mm')} - Status: ${
                booking.status
              } - Is future: ${isFuture}`
            )
            return isFuture
          }
          return false
        })

        console.log('🔍 Future bookings found:', futureBookings.length)

        if (futureBookings.length > 0) {
          const sortedBookings = sortBookingsByDateTime(futureBookings)
          const next = sortedBookings[0]
          console.log('🎯 Next appointment selected:', {
            id: next._id,
            practitioner: next.medicalProfName,
            date: next.bookingDate,
            time: next.startTime
          })
          setNextAppointment(next)
        } else {
          console.log('❌ No future appointments found')
          setNextAppointment(null)
        }
      } else {
        console.log('❌ No bookings returned from API')
        setNextAppointment(null)
      }
    } catch (e) {
      console.error('Error fetching next appointment:', e)
      setError(handleError(e, true))
      setNextAppointment(null)
    }

    setLoading(false)
  }

  useEffect(() => {
    fetchNextAppointment()
    // eslint-disable-next-line
  }, [currentUser, state.bookings.data, state.clinics.data, state.services.data, state.virtualServices.data])

  return {
    nextAppointment,
    loading,
    error,
    refetch: fetchNextAppointment
  }
}

export default useNextAppointment
